import { DestroyRef, Inject, Injectable, Signal, computed, inject } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { Store } from "@ngrx/store";
import { DomMutation } from "shared/dom-mutation-core";
import { CanvasInteractionEvent, DriverscamVideoMinimumInfo, Hotspot, IDisplaySlide, MinimumSlideInformation, QuestionIdentifier, SlideTypeCategory } from "@pcp/core/interfaces";
import {
    PlaybackPositionChangedEvent,
    VideoPlayerComponentEvent,
} from "@pcp/streaming";
// LayerSignals (per-layer slide identity) + BookpageSignals are owned by
// ActiveElementUiStateService; reads route through `this.uiState.*LayerSignals`
// and `this.uiState.bookpageSignals`.
import { EMPTY, Observable, Subject, of } from "rxjs";
import { debounceTime, filter, map, pairwise, throttleTime } from "rxjs/operators";
import { MutationCommand, SlidePreviewCommandStreamService } from "./slide-preview-command-stream.service";
import {
    createVideoSlide,
    ICheckIsFavoriteService,
} from "shared/classroom/preview-screen/core";
import {
    ClassroomEvents,
    LessonCommandFacade,
} from "shared/classroom/data-access";
import { ClassroomEmitGateService } from "./classroom-emit-gate.service";
import { SLIDE_SYNC_CHECK_IS_FAVORITE_SERVICE } from "../injection-tokens";
import { ActiveElementUiStateService, ClassroomFacade } from "shared/classroom/data-access";
import { toObservable } from "@angular/core/rxjs-interop";

type Layer = 1 | 2 | 3;

/**
 * Throttle window (ms) for beamer→teacher preview-navigation intents — one send per
 * window, leading edge. Replicates the `throttleTime(500)` the deleted emitter effects used.
 */
const PREVIEW_INTENT_THROTTLE_MS = 500;

type LayerProps = {
    suffix: '-primary' | '-secondary' | '-tertiary';
    slide$: Observable<IDisplaySlide | null>;
    isHidden$: Observable<boolean>;
    slideTypeCategory$: Observable<SlideTypeCategory>;
    slideIsFavorite$: Observable<boolean>;
    slidePageNumber$: Observable<number | undefined>;

    isOpenedFromHotspot$: Observable<boolean>;
    favoritesDisabled$: Observable<boolean>;
    showPrevBookpagePageLink$: Observable<boolean>;
    showNextBookpagePageLink$: Observable<boolean>;

    resetDynamicContent$: Observable<{ resetImageToBasic: boolean }>;
    /**
     * Whether this layer's current perception image is revealed (synced Y.Doc reveal-state).
     * Drives the canvas perception slide's pin/fade and restores correctly on late-join /
     * refresh; resets on show / content-reset so re-showing re-hides.
     */
    perceptionRevealed$: Observable<boolean>;
    /**
     * This layer's perception "show epoch" — bumped on a same-slide content reset so the
     * canvas perception slide replays its hide animation on reset.
     */
    perceptionShowEpoch$: Observable<number>;
    /**
     * Per-layer mutation command stream, exposed as a Signal of Observable.
     *
     * Why a Signal-of-Observable instead of `Observable<Observable<MutationCommand>>` + async pipe:
     * the underlying outer-stream emits new inner ReplaySubjects from SignalR message handlers
     * (Y.applyUpdate callbacks) running outside Angular's execution context. In zoneless mode,
     * async-pipe's `markForCheck` signal is unreliably propagated to the slide-preview-element's
     * `[commandStream$]` input binding for emissions originating outside Angular's CD cycle —
     * the initial subscription works (it happens inside CD) but subsequent emissions (e.g. on
     * same-slide reset's `mutationBatchesCleared$`) don't trigger a binding update. Signals
     * are part of Angular's reactivity graph and trigger CD reliably.
     */
    mutationCommandStream: Signal<Observable<MutationCommand> | undefined>;

    onPerceptionImageRevealed: (slide: IDisplaySlide) => void;
    onHotspotClick: (event: Hotspot, slide: IDisplaySlide) => void;

    /**
     * HTML-element slide has been modified (interactive iframe DOM mutation).
     */
    onSlideMutated: (slide: IDisplaySlide, mutations: DomMutation[]) => void;

    /**
     * Called on interactions on rubber elements shown as primary slide.
     */
    onCanvasMutated: (slide: IDisplaySlide, mutation: CanvasInteractionEvent) => void;

    onVideoStartedPlaying: ($event: PlaybackPositionChangedEvent, slide: IDisplaySlide) => void;
    onVideoPaused: ($event: VideoPlayerComponentEvent, slide: IDisplaySlide) => void;
    onVideoLoaded: ($event: VideoPlayerComponentEvent, slide: IDisplaySlide) => void;
    onVideoSeekedToPosition: ($event: PlaybackPositionChangedEvent, slide: IDisplaySlide) => void;
    onVideoEnded: ($event: any, slide: IDisplaySlide) => void;

    onSvgTextboardPageNumberChanged: (pageNumber: number) => void;

    onStartVideoInVideoQuestion: (videoId: number) => void;
};

@Injectable()
export class SlidePreviewFacade {
    private readonly lessonYjs = inject(ClassroomFacade);
    private readonly uiState = inject(ActiveElementUiStateService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly lessonCmd = inject(LessonCommandFacade);
    private readonly emitGate = inject(ClassroomEmitGateService);

    /**
     * Coalesces rapid textboard page changes (e.g. user holding "next page") before writing them
     * to Y.Doc. The local display still updates immediately via the dispatched NgRx action — this
     * Subject only governs the Y.Doc write rate.
     *
     * Issue #22502: 100ms debounce as a safety guard against pathological infinite-switching loops.
     */
    private readonly textboardPageWrite$ = new Subject<{ layer: 1 | 2 | 3; pageNumber: number }>();

    /**
     * Beamer/student → teacher navigation intents. Each Subject is throttled independently
     * (one send per 500ms, leading edge) and gated on `canSendIntent`, replicating the
     * deleted emitter effects. All outbound SignalR emission lives in the facade now.
     */
    private readonly previewSlideIntent$ = new Subject<MinimumSlideInformation>();
    private readonly driverscamIntent$ = new Subject<DriverscamVideoMinimumInfo>();
    private readonly difficultQuestionIntent$ = new Subject<QuestionIdentifier[]>();

    constructor(
        private readonly store: Store,
        @Inject(SLIDE_SYNC_CHECK_IS_FAVORITE_SERVICE)
        private readonly isFavoriteService: ICheckIsFavoriteService,
        readonly slideCommandStream: SlidePreviewCommandStreamService,
    ) {
        this.textboardPageWrite$
            .pipe(debounceTime(100), takeUntilDestroyed(this.destroyRef))
            .subscribe(({ layer, pageNumber }) =>
                this.lessonYjs.flipPage(layer, pageNumber)
            );

        this.previewSlideIntent$
            .pipe(throttleTime(PREVIEW_INTENT_THROTTLE_MS), takeUntilDestroyed(this.destroyRef))
            .subscribe((slide) => {
                if (this.emitGate.canSendIntent()) {
                    void this.lessonCmd.sendIntent.navigateToPreviewSlide(slide);
                }
            });

        this.driverscamIntent$
            .pipe(throttleTime(PREVIEW_INTENT_THROTTLE_MS), takeUntilDestroyed(this.destroyRef))
            .subscribe((driverscam) => {
                if (this.emitGate.canSendIntent()) {
                    void this.lessonCmd.sendIntent.navigateToPreviewDriverscam(driverscam);
                }
            });

        this.difficultQuestionIntent$
            .pipe(throttleTime(PREVIEW_INTENT_THROTTLE_MS), takeUntilDestroyed(this.destroyRef))
            .subscribe((difficultQuestionNumbers) => {
                if (this.emitGate.canSendIntent()) {
                    void this.lessonCmd.sendIntent.showDifficultQuestionElement(difficultQuestionNumbers);
                }
            });
    }

    /**
     * Beamer → teacher: request navigation to a thumbnail in the next-slides preview area.
     * Throttled + gated via `previewSlideIntent$`.
     */
    onNextPreviewSlideClicked(slide: MinimumSlideInformation): void {
        this.previewSlideIntent$.next(slide);
    }

    /** Beamer → teacher: request showing a driverscam assignment from the preview area. */
    onDriverscamAssignmentClicked(driverscam: DriverscamVideoMinimumInfo): void {
        this.driverscamIntent$.next(driverscam);
    }

    /** Beamer → teacher: request showing a difficult-question element for the given numbers. */
    onDifficultQuestionIndicatorClicked(difficultQuestionNumbers: QuestionIdentifier[]): void {
        this.difficultQuestionIntent$.next(difficultQuestionNumbers);
    }

    /**
     * Emits `{ resetImageToBasic: true }` when the primary slide should reset to its
     * initial state (restart video, reset rubber element, reset HTML-element board).
     *
     * Keyed on a **same-slide showing change**. `<layer>SlideShowingId` is re-minted by
     * `resetLayerForNewShowing` on every fresh showing — `setSlide`, `removeSlide` and
     * `resetSlideToInitial` alike — as a Y.Map field in the same transaction, so it
     * reaches the beamer through the ordinary state pipeline and is symmetric on writer
     * and receiver. The slide-id gate keeps navigation out: that re-mints the showing id
     * too, and reacting to it would tear down the freshly rendered new slide.
     *
     * This deliberately does NOT use `mutationBatchesCleared$` / `dynamicContentCleared$`.
     * Those fire only when a clear actually empties a non-empty Y.Array — a clear that
     * deletes nothing produces no delta — so they were silent for exactly the slides whose
     * state lives outside the Y.Doc: a rubber element's erase strokes are SignalR-ephemeral
     * and a video's playback position lives in the player. Reset did nothing for them
     * (refs #32828). Both events are also strict subsets of a showing change today
     * (`clearMutations`/`clearDynamicContent` are only reached from `resetLayerForNewShowing`,
     * and `ClassroomFacade.resetDynamicContent()` has no callers), so listening to them as
     * well would just fire the teardown twice per reset.
     *
     * The cross-feature `ClassroomEvents.userResetActiveSlide` NgRx event is still
     * dispatched (workspace-reducer cleanup, toolbar reset, driverscam stop) but is not
     * wired in here — it is an event bus, not a slide-layer trigger.
     *
     * Only the primary layer emits — secondary / tertiary layers have no "reset to
     * initial" UX entry point in the workspace.
     */
    private createResetDynamicContent$(layer: Layer): Observable<{ resetImageToBasic: boolean }> {
        if (layer !== 1) return EMPTY;
        const showing = computed(() => ({
            slideId: this.lessonYjs.primarySlide()?.id ?? null,
            showingId: this.lessonYjs.primarySlideShowingId(),
        }));
        return toObservable(showing).pipe(
            pairwise(),
            filter(([prev, curr]) =>
                curr.slideId !== null
                && curr.slideId === prev.slideId
                && curr.showingId !== prev.showingId
            ),
            map(() => ({ resetImageToBasic: true })),
        );
    }

    /**
     * Per-layer "is the current perception image revealed?" stream, from the synced Y.Doc
     * reveal-state, so the canvas perception slide pins itself visible once revealed and
     * restores that on late-join — while re-hiding on every show / content-reset (the Y.Doc
     * field resets in those cascades).
     */
    private createPerceptionRevealed$(layer: Layer): Observable<boolean> {
        return toObservable(computed(() => this.lessonYjs.revealStateForLayer(layer)().revealed));
    }

    /**
     * Per-layer perception "show epoch" stream. Bumps on a same-slide content reset so the
     * canvas perception slide re-runs its hide animation even when the slide was not revealed
     * (a plain `revealed:false` re-write would be memoized away and never reach the beamer).
     */
    private createPerceptionShowEpoch$(layer: Layer): Observable<number> {
        return toObservable(computed(() => this.lessonYjs.revealStateForLayer(layer)().epoch));
    }

    /**
     * Reveal a perception image by writing the synced Y.Doc reveal-state (source of truth,
     * restored on late-join). The Y.Doc write reveals the slide on every client (teacher writes;
     * beamer/students/slide-consumer read); writer-role is enforced at the Y.Doc service boundary.
     */
    private revealPerceptionImage(layer: Layer): void {
        this.lessonYjs.revealPerceptionSlide(layer);
    }

    readonly primaryLayer: LayerProps = {
        suffix: '-primary',
        slide$: toObservable(this.uiState.primaryLayerSignals.slide),
        isHidden$: toObservable(this.uiState.isHiddenForLayer(1)),
        isOpenedFromHotspot$: toObservable(this.uiState.primaryLayerSignals.isAccessedViaHotspot),
        slideTypeCategory$: toObservable(this.uiState.primaryLayerSignals.slideTypeCategory),
        slideIsFavorite$: this.isFavoriteService.isSlideAddedToFavorites(
            toObservable(this.uiState.primaryLayerSignals.slideIdWithType),
        ),
        slidePageNumber$: toObservable(this.uiState.effectiveTextboardPageForLayer(1)),
        favoritesDisabled$: toObservable(this.uiState.primaryLayerSignals.favoritesDisabled),
        showPrevBookpagePageLink$: of(false),
        showNextBookpagePageLink$: of(false),
        mutationCommandStream: toSignal(this.slideCommandStream.createPrimarySlideMutationCommandStream()),
        resetDynamicContent$: this.createResetDynamicContent$(1),
        perceptionRevealed$: this.createPerceptionRevealed$(1),
        perceptionShowEpoch$: this.createPerceptionShowEpoch$(1),
        onPerceptionImageRevealed: () => {
            this.revealPerceptionImage(1);
        },
        onHotspotClick: (event: Hotspot, slide: IDisplaySlide) => {
            this.store.dispatch(
                ClassroomEvents.hotspotActivated({
                    hotspot: event,
                    slide,
                }),
            );
        },
        onSlideMutated: (_slide: IDisplaySlide, mutations: DomMutation[]) => {
            this.lessonYjs.appendMutationBatch(1, JSON.stringify(mutations));
        },
        onCanvasMutated: (slide: IDisplaySlide, mutation: CanvasInteractionEvent) => {
            if (!this.emitGate.canEmitRubber()) return;
            void this.lessonCmd.broadcast.rubberElementMutation(1, mutation, slide);
        },
        onVideoStartedPlaying: ($event: PlaybackPositionChangedEvent, slide: IDisplaySlide) => {
            if (!this.emitGate.canEmitVideo()) return;
            const { seconds: playbackStartPosition } = $event;
            void this.lessonCmd.broadcast.videoStartedPlaying(1, slide, playbackStartPosition);
        },
        onVideoPaused: ($event: VideoPlayerComponentEvent, slide: IDisplaySlide) => {
            if (!this.emitGate.canEmitVideo()) return;
            void this.lessonCmd.broadcast.videoPaused(1, slide);
        },
        onVideoLoaded: ($event: VideoPlayerComponentEvent, slide: IDisplaySlide) => {
            if (!this.emitGate.canEmitVideo()) return;
            void this.lessonCmd.broadcast.videoLoaded(1, slide);
        },
        onVideoSeekedToPosition: ($event: PlaybackPositionChangedEvent, slide: IDisplaySlide) => {
            if (!this.emitGate.canEmitVideo()) return;
            const { isInPausedState, seconds: seekedToPosition } = $event;
            void this.lessonCmd.broadcast.videoSeeked(1, slide, seekedToPosition, isInPausedState);
        },
        onSvgTextboardPageNumberChanged: (pageNumber: number) => {
            this.uiState.setLocalTextboardPage(1, pageNumber);
            this.textboardPageWrite$.next({ layer: 1, pageNumber });
        },
        onStartVideoInVideoQuestion: (videoId: number) => {
            this.lessonYjs.showSlide(2, createVideoSlide(videoId, true));
        },
        onVideoEnded: ($event: any, slide: IDisplaySlide) => {
            // empty
        },
    };

    readonly secondaryLayer: LayerProps = {
        suffix: '-secondary',
        slide$: toObservable(this.uiState.secondaryLayerSignals.slide),
        isHidden$: toObservable(this.uiState.isHiddenForLayer(2)),
        isOpenedFromHotspot$: toObservable(this.uiState.secondaryLayerSignals.isAccessedViaHotspot),
        slideTypeCategory$: toObservable(this.uiState.secondaryLayerSignals.slideTypeCategory),
        slideIsFavorite$: this.isFavoriteService.isSlideAddedToFavorites(
            toObservable(this.uiState.secondaryLayerSignals.slideIdWithType),
        ),
        slidePageNumber$: toObservable(this.uiState.effectiveTextboardPageForLayer(2)),
        favoritesDisabled$: toObservable(this.uiState.secondaryLayerSignals.favoritesDisabled),
        showPrevBookpagePageLink$: toObservable(this.uiState.bookpageSignals.hasPreviousBookpage),
        showNextBookpagePageLink$: toObservable(this.uiState.bookpageSignals.hasNextBookpage),
        mutationCommandStream: toSignal(this.slideCommandStream.createSecondarySlideMutationCommandStream()),
        resetDynamicContent$: EMPTY,
        perceptionRevealed$: this.createPerceptionRevealed$(2),
        perceptionShowEpoch$: this.createPerceptionShowEpoch$(2),
        onPerceptionImageRevealed: () => {
            this.revealPerceptionImage(2);
        },
        onHotspotClick: (event: Hotspot, slide: IDisplaySlide) => {
            this.store.dispatch(
                ClassroomEvents.hotspotActivated({
                    hotspot: event,
                    slide,
                }),
            );
        },
        onSlideMutated: (_slide: IDisplaySlide, mutations: DomMutation[]) => {
            this.lessonYjs.appendMutationBatch(2, JSON.stringify(mutations));
        },
        onCanvasMutated: (slide: IDisplaySlide, mutation: CanvasInteractionEvent) => {
            if (!this.emitGate.canEmitRubber()) return;
            void this.lessonCmd.broadcast.rubberElementMutation(2, mutation, slide);
        },
        onVideoStartedPlaying: ($event: PlaybackPositionChangedEvent, slide: IDisplaySlide) => {
            if (!this.emitGate.canEmitVideo()) return;
            const { seconds: playbackStartPosition } = $event;
            void this.lessonCmd.broadcast.videoStartedPlaying(2, slide, playbackStartPosition);
        },
        onVideoPaused: ($event: VideoPlayerComponentEvent, slide: IDisplaySlide) => {
            if (!this.emitGate.canEmitVideo()) return;
            void this.lessonCmd.broadcast.videoPaused(2, slide);
        },
        onVideoLoaded: ($event: VideoPlayerComponentEvent, slide: IDisplaySlide) => {
            if (!this.emitGate.canEmitVideo()) return;
            void this.lessonCmd.broadcast.videoLoaded(2, slide);
        },
        onVideoSeekedToPosition: ($event: PlaybackPositionChangedEvent, slide: IDisplaySlide) => {
            if (!this.emitGate.canEmitVideo()) return;
            const { isInPausedState, seconds: seekedToPosition } = $event;
            void this.lessonCmd.broadcast.videoSeeked(2, slide, seekedToPosition, isInPausedState);
        },
        onSvgTextboardPageNumberChanged: (pageNumber: number) => {
            this.uiState.setLocalTextboardPage(2, pageNumber);
            this.textboardPageWrite$.next({ layer: 2, pageNumber });
        },
        onStartVideoInVideoQuestion: (videoId: number) => {
            this.lessonYjs.showSlide(3, createVideoSlide(videoId, true));
        },
        onVideoEnded: ($event: any, slide: IDisplaySlide) => {
            if (slide.autoClose === true) {
                this.lessonYjs.closeLayer(2);
            }
        },
    };

    readonly tertiaryLayer: LayerProps = {
        suffix: '-tertiary',
        slide$: toObservable(this.uiState.tertiaryLayerSignals.slide),
        isHidden$: toObservable(this.uiState.isHiddenForLayer(3)),
        isOpenedFromHotspot$: toObservable(this.uiState.tertiaryLayerSignals.isAccessedViaHotspot),
        slideTypeCategory$: toObservable(this.uiState.tertiaryLayerSignals.slideTypeCategory),
        slideIsFavorite$: this.isFavoriteService.isSlideAddedToFavorites(
            toObservable(this.uiState.tertiaryLayerSignals.slideIdWithType),
        ),
        slidePageNumber$: toObservable(this.uiState.effectiveTextboardPageForLayer(3)),
        favoritesDisabled$: toObservable(this.uiState.tertiaryLayerSignals.favoritesDisabled),
        showPrevBookpagePageLink$: of(false),
        showNextBookpagePageLink$: of(false),
        mutationCommandStream: toSignal(this.slideCommandStream.createTertiarySlideMutationCommandStream()),
        resetDynamicContent$: EMPTY,
        perceptionRevealed$: this.createPerceptionRevealed$(3),
        perceptionShowEpoch$: this.createPerceptionShowEpoch$(3),
        onPerceptionImageRevealed: () => {
            this.revealPerceptionImage(3);
        },
        onHotspotClick: (event: Hotspot, slide: IDisplaySlide) => {
            this.store.dispatch(
                ClassroomEvents.hotspotActivated({
                    hotspot: event,
                    slide,
                }),
            );
        },
        onSlideMutated: (_slide: IDisplaySlide, mutations: DomMutation[]) => {
            this.lessonYjs.appendMutationBatch(3, JSON.stringify(mutations));
        },
        onCanvasMutated: (slide: IDisplaySlide, mutation: CanvasInteractionEvent) => {
            if (!this.emitGate.canEmitRubber()) return;
            void this.lessonCmd.broadcast.rubberElementMutation(3, mutation, slide);
        },
        onVideoStartedPlaying: ($event: PlaybackPositionChangedEvent, slide: IDisplaySlide) => {
            if (!this.emitGate.canEmitVideo()) return;
            const { seconds: playbackStartPosition } = $event;
            void this.lessonCmd.broadcast.videoStartedPlaying(3, slide, playbackStartPosition);
        },
        onVideoPaused: ($event: VideoPlayerComponentEvent, slide: IDisplaySlide) => {
            if (!this.emitGate.canEmitVideo()) return;
            void this.lessonCmd.broadcast.videoPaused(3, slide);
        },
        onVideoLoaded: ($event: VideoPlayerComponentEvent, slide: IDisplaySlide) => {
            if (!this.emitGate.canEmitVideo()) return;
            void this.lessonCmd.broadcast.videoLoaded(3, slide);
        },
        onVideoSeekedToPosition: ($event: PlaybackPositionChangedEvent, slide: IDisplaySlide) => {
            if (!this.emitGate.canEmitVideo()) return;
            const { isInPausedState, seconds: seekedToPosition } = $event;
            void this.lessonCmd.broadcast.videoSeeked(3, slide, seekedToPosition, isInPausedState);
        },
        onSvgTextboardPageNumberChanged: (pageNumber: number) => {
            throw new Error('onSvgTextboardPageNumberChanged not implemented for tertiary slides');
        },
        onStartVideoInVideoQuestion: (videoId: number) => {
            throw new Error('onStartVideoInVideoQuestion not implemented for tertiary slides');
        },
        onVideoEnded: ($event: any, slide: IDisplaySlide) => {
            if (slide.autoClose === true) {
                this.lessonYjs.closeLayer(3);
            }
        },
    };

    readonly layers = [this.primaryLayer, this.secondaryLayer, this.tertiaryLayer];
}
